import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import * as api from "../../api/api";

export const loginThunk = createAsyncThunk("users/login", async (payload, { rejectWithValue }) => {
  try {
    const res = await api.login(payload);
    localStorage.setItem("accessToken", res.data.accessToken);
    localStorage.setItem("refreshToken", res.data.refreshToken);
    return res.data.user;
  } catch (err) {
    return rejectWithValue(err?.message || "Login failed");
  }
});

export const logoutThunk = createAsyncThunk("users/logout", async () => {
  // Always clear local session state, even if the server call fails (e.g. an
  // already-expired/invalid token) — logout must never get "stuck" client-side.
  try {
    await api.logout();
  } catch (err) {
    // ignore — token may already be invalid/expired server-side
  } finally {
    localStorage.removeItem("accessToken");
    localStorage.removeItem("refreshToken");
  }
});

const usersSlice = createSlice({
  name: "users",
  initialState: {
    user: null,
    is_login: false,
    is_loading: false,
    error_message: null,
  },
  reducers: {
    clearUserState: (state) => {
      state.user = null;
      state.is_login = false;
      state.error_message = null;
    },
    // Optimistic local mirror of POST /auth/tutorials/:id/seen — updated
    // right when the user clicks Okay so the walkthrough doesn't flash back
    // open on a re-render before the request resolves. The server call is
    // still what makes it stick across devices/logins.
    markTutorialSeenLocally: (state, action) => {
      if (!state.user) return;
      const seen = new Set(state.user.seen_tutorials || []);
      seen.add(action.payload);
      state.user.seen_tutorials = [...seen];
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(loginThunk.pending, (state) => {
        state.is_loading = true;
        state.error_message = null;
      })
      .addCase(loginThunk.fulfilled, (state, action) => {
        state.is_loading = false;
        state.is_login = true;
        state.user = action.payload;
      })
      .addCase(loginThunk.rejected, (state, action) => {
        state.is_loading = false;
        state.is_login = false;
        state.error_message = action.payload;
      })
      .addCase(logoutThunk.fulfilled, (state) => {
        state.user = null;
        state.is_login = false;
      });
  },
});

export const { clearUserState, markTutorialSeenLocally } = usersSlice.actions;
export default usersSlice.reducer;
